import { CHAT_TYPES } from "@constants/chat";
import { CONVERSATION_COLlECTION, MESSAGE_COLlECTION } from "@fb/const";
import { db } from "@fb/db";
import {
  addDoc,
  collection,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  documentId,
} from "firebase/firestore";

import { AssistantMessageType } from "@components/AssistantChat/type";

import { MessageType } from "./type";

type NewMessage = {
  message: MessageType;
  conversationId: string;
  userId: string;
};

export async function addNewMessage(newMessage: NewMessage) {
  try {
    const docRef = await addDoc(collection(db, MESSAGE_COLlECTION), {
      ...newMessage.message,
      conversationId: newMessage.conversationId,
      userId: newMessage.userId,
      date: serverTimestamp(),
    });
    return docRef;
  } catch (e) {
    console.error("Error adding message: ", e);
  }
}

export async function getFirstMessage(
  conversationIds: string[]
): Promise<AssistantMessageType[]> {
  try {
    const promises = conversationIds.map(async (conversationId) => {
      const q = query(
        collection(db, MESSAGE_COLlECTION),
        where("conversationId", "==", conversationId),
        orderBy("date", "asc"),
        limit(1)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    });

    const results = await Promise.all(promises);
    return results as AssistantMessageType[];
  } catch (error) {
    console.error("Error getting latest messages:", error);
    return [];
  }
}

export async function deleteMessages(
  conversationId: string,
  userId: string
): Promise<boolean> {
  try {
    const q = query(
      collection(db, MESSAGE_COLlECTION),
      where("userId", "==", userId),
      where("conversationId", "==", conversationId)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return true; // No messages to delete
    }

    const deletePromises = querySnapshot.docs.map(async (doc) => {
      await deleteDoc(doc.ref);
    });

    await Promise.all(deletePromises);
    return true;
  } catch (error) {
    console.error("Error deleting messages:", error);
    return false;
  }
}

export async function findOrCreateNewConversation(conversationId: string) {
  if (!conversationId) {
    return;
  }
  const userId = conversationId.includes("-assistant-")
    ? conversationId?.split("-assistant-")[0]
    : conversationId?.split("-assistant")[0];
  try {
    const docRef = doc(db, CONVERSATION_COLlECTION, conversationId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // update doc data with new session
      await updateDoc(docRef, {
        session: new Date().getTime(),
        conversationId, // Add if this field is missing
      });

      return {
        conversationId,
        docRef: docSnap.data(),
      };
    } else {
      const newDocRef = await setDoc(docRef, {
        conversationId,
        participants: [userId],
        date: +new Date(),
        session: new Date().getTime(),
        type: CHAT_TYPES.ASSISTANT,
      });
      return {
        conversationId,
        docRef: newDocRef,
      };
    }
  } catch (e) {
    console.error("Error add new conversation: ", e);
  }
}

export async function deleteConversation(
  conversationId: string,
  type: keyof typeof CHAT_TYPES
): Promise<boolean> {
  const userId = conversationId.includes("-assistant-")
    ? conversationId.split("-assistant-")[0]
    : conversationId.split("-assistant")[0];

  if (!userId) {
    return false; // Invalid conversation ID
  }

  try {
    const q = query(
      collection(db, CONVERSATION_COLlECTION),
      where("participants", "array-contains-any", [userId]),
      where(documentId(), "in", [conversationId]),
      where("type", "==", type)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      console.log("No conversation found");
      return true; // No conversation to delete
    }

    await deleteMessages(conversationId, userId); // Delete associated messages

    const deletePromises = querySnapshot.docs.map(async (doc) => {
      await deleteDoc(doc.ref);
    });
    await Promise.all(deletePromises);
    return true;
  } catch (error) {
    console.error("Error deleting conversation:", error);
    return false;
  }
}

export async function deleteMultiConversation(
  conversationIds: string[],
  type: keyof typeof CHAT_TYPES
): Promise<boolean> {
  try {
    const promises = conversationIds.map((conversationId) => {
      return deleteConversation(conversationId, type);
    });
    await Promise.all(promises);
    return true;
  } catch (error) {
    console.error("Error deleting multi conversation:", error);
    return false;
  }
}
